import { GlobalErrorView } from '@client/components/_core/error-handling/GlobalErrorView';
import { useErrorRouteContext } from '@client/components/_core/error-handling/useErrorRouteContext';
import { getConfig } from '@client/config/runtime';
import { useAuthStore } from '@client/stores/AuthStore';
import { isCaptureLeftToServer } from '@client/trpc/error-classification';
import { isAuthRoute } from '@client/utils';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

type Props = Parameters<Sentry.FallbackRender>[0];

export function ErrorFallback({ error, resetError }: Props) {
    const { isPublicSection, orgId } = useErrorRouteContext();

    // Ignore legacy expired-token errors on auth routes, where a notification is already shown.
    const suppressFallback =
        getConfig('ENV', import.meta.env.VITE_ENV) === 'development' ||
        (useAuthStore.getState().authTokenExpired && isAuthRoute());

    // Capture on error changes only; context rerenders must not report the same error again. A
    // refusal the server marked `clientCapture: false` is the server's to report (CUR-1962).
    useEffect(() => {
        if (error && !suppressFallback && !isCaptureLeftToServer(error)) {
            Sentry.captureException(error, {
                tags: { location: 'ErrorFallback' },
                level: 'error',
                extra: {
                    orgId,
                    isPublicSection,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                },
            });
        }
    }, [error]);

    if (suppressFallback) {
        return <div />;
    }

    return <GlobalErrorView isPublicSection={isPublicSection} orgId={orgId} error={error} reset={resetError} />;
}
